import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getOne, list, update, create, remove } from "@/lib/api";
import api, { fileUrl, uploadFile } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileField } from "@/components/ResourcePage";
import { fmtDate, fmtIDR, daysUntil, VENDOR_CATEGORIES, CONFIRMATION_STATUSES, EVENT_STATUSES } from "@/lib/constants";
import { ArrowLeft, CalendarDays, MapPin, User, Zap, Phone, MessageCircle, Play, Gem, CheckCircle2, X, Send, Star, UserPlus, Trash2, Plus, Crown, Camera, Pencil, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const TABS = ["Overview", "Client", "Team", "Vendor", "Rundown", "Activity", "Timeline", "Payment", "Documents", "Notes"];

const TIMELINE = [
  ["T-180", "Booking vendor utama", 180], ["T-120", "Finalisasi konsep", 120], ["T-90", "Vendor meeting", 90],
  ["T-60", "Technical meeting", 60], ["T-30", "Final guest count", 30], ["T-14", "Final rundown", 14],
  ["T-7", "Final confirmation vendor", 7], ["T-1", "Final preparation", 1], ["T-0", "Wedding Day", 0], ["T+1", "Event closing / report", -1],
];

const waLink = (phone) => `https://wa.me/${String(phone || "").replace(/[^0-9]/g, "")}`;
const photoSrc = (p) => (!p ? "" : (String(p).startsWith("http") ? p : fileUrl(p)));
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [event, setEvent] = useState(null);
  const [client, setClient] = useState(null);
  const [data, setData] = useState({ checklists: [], rundown: [], event_vendors: [], event_team: [], guests: [], payments: [], documents: [], tasks: [], timeline: [] });
  const [masterTeam, setMasterTeam] = useState([]);
  const [masterVendors, setMasterVendors] = useState([]);
  const [notes, setNotes] = useState("");
  const [tlLabel, setTlLabel] = useState("");
  const [tlOffset, setTlOffset] = useState("");
  const [teamOpen, setTeamOpen] = useState(false);
  const [acc, setAcc] = useState(null);
  const [accPwd, setAccPwd] = useState("");
  const [accSaving, setAccSaving] = useState(false);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [tlEdit, setTlEdit] = useState(null);
  const [crewAccountIds, setCrewAccountIds] = useState(new Set());

  const loadCrewAccounts = () => api.get("/crew-account-ids").then((r) => setCrewAccountIds(new Set(r.data.member_ids || []))).catch(() => {});

  const loadAll = async () => {
    const ev = await getOne("events", id).catch(() => null);
    setEvent(ev); setNotes(ev?.notes || "");
    if (ev?.client_id) getOne("clients", ev.client_id).then(setClient).catch(() => {});
    const colls = ["checklists", "rundown", "event_vendors", "event_team", "guests", "payments", "documents", "tasks", "timeline"];
    const res = {};
    await Promise.all(colls.map((c) => list(c, { event_id: id }).then((d) => { res[c] = d; }).catch(() => { res[c] = []; })));
    setData(res);
    if (can(3)) {
      list("team_members").then(setMasterTeam).catch(() => {});
      list("vendors").then(setMasterVendors).catch(() => {});
      loadCrewAccounts();
    }
  };
  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line

  const saveNotes = async () => { await update("events", id, { notes }); toast.success("Catatan disimpan"); };

  const addTimeline = async () => {
    if (!tlLabel.trim()) { toast.error("Nama milestone wajib diisi"); return; }
    if (tlEdit) {
      await update("timeline", tlEdit.id, { label: tlLabel, offset: Number(tlOffset || 0) });
      toast.success("Milestone diperbarui"); setTlEdit(null);
    } else {
      await create("timeline", { event_id: id, label: tlLabel, offset: Number(tlOffset || 0), custom: true });
      toast.success("Milestone ditambahkan");
    }
    setTlLabel(""); setTlOffset(""); loadAll();
  };
  const startEditTl = (item) => { setTlEdit(item); setTlLabel(item.label); setTlOffset(String(item.off)); };
  const cancelEditTl = () => { setTlEdit(null); setTlLabel(""); setTlOffset(""); };
  const initTimeline = async () => {
    try { await api.post(`/events/${id}/timeline/init`); toast.success("Timeline siap diedit"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal inisialisasi timeline"); }
  };
  const uploadCover = async (fileList) => {
    const f = (fileList || [])[0]; if (!f) return;
    setUploadingCover(true);
    try { const res = await uploadFile(f); await update("events", id, { cover: res.storage_path }); toast.success("Poster event diperbarui"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal mengunggah poster"); }
    finally { setUploadingCover(false); }
  };
  const setCoverPos = async (pos) => {
    setEvent((p) => ({ ...p, cover_position: pos }));
    try { await update("events", id, { cover_position: pos }); } catch { loadAll(); }
  };
  const changeStatus = async (s) => {
    setEvent((p) => ({ ...p, status: s }));
    try { await update("events", id, { status: s }); toast.success("Status event diperbarui"); } catch { toast.error("Gagal ubah status"); loadAll(); }
  };
  const [editContract, setEditContract] = useState(false);
  const [contractVal, setContractVal] = useState("");
  const saveContract = async () => {
    try { await update("events", id, { total_price: Number(contractVal || 0) }); toast.success("Nilai contract diperbarui"); setEditContract(false); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan contract"); }
  };
  const delTimeline = async (tid) => { await remove("timeline", tid); toast.success("Dihapus"); loadAll(); };

  const remindVendor = async (evId) => {
    try { const r = await api.get(`/reminders/vendor/${evId}/link`); window.open(r.data.wa_link, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuka WhatsApp"); }
  };
  const remindPayment = async (pId) => {
    try { const r = await api.get(`/reminders/payment/${pId}/link`); window.open(r.data.wa_link, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuka WhatsApp"); }
  };
  const makeReceipt = async (pId) => {
    try { const r = await api.post(`/receipts/generate/${pId}`); toast.success(`Kuitansi ${r.data.receipt_number} diterbitkan (menu Kuitansi)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat kuitansi"); }
  };
  const togglePM = async (m) => { await update("event_team", m.id, { is_pm: !m.is_pm }); loadAll(); };
  const delTeam = async (tid) => { await remove("event_team", tid); toast.success("Tim dihapus dari event"); loadAll(); };
  const openAccount = async (m) => {
    setAccPwd("");
    let info = { exists: false, email: "" };
    try { info = (await api.get(`/team_members/${m.member_id}/account`)).data; } catch { /* noop */ }
    setAcc({ member_id: m.member_id, name: m.member_name, exists: info.exists, email: info.email || "" });
  };
  const saveAccount = async () => {
    if (!acc.email.trim() || accPwd.length < 6) { toast.error("Email valid & password minimal 6 karakter"); return; }
    setAccSaving(true);
    try {
      await api.post(`/team_members/${acc.member_id}/account`, { email: acc.email.trim(), password: accPwd });
      toast.success("Akun login crew berhasil disimpan");
      setAcc(null);
      loadCrewAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat akun"); }
    finally { setAccSaving(false); }
  };
  const delVendor = async (vid) => { await remove("event_vendors", vid); toast.success("Vendor dihapus dari event"); loadAll(); };
  const setVendorConfirm = async (ev, status) => { await update("event_vendors", ev.id, { confirmation: status }); loadAll(); };

  const genInvoice = async (force = false) => {
    try {
      const inv = await api.post(`/invoices/generate/${id}${force ? "?force=true" : ""}`).then((r) => r.data);
      toast.success(`Invoice ${inv.invoice_number} dibuat`); navigate("/invoices");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(e.response.data.detail, { description: "Buat invoice baru untuk event ini?", action: { label: "Tetap Buat", onClick: () => genInvoice(true) }, duration: 9000 });
      } else { toast.error(e.response?.data?.detail || "Gagal membuat invoice"); }
    }
  };

  const cd = event ? daysUntil(event.wedding_date) : null;
  const doneAct = data.tasks.filter((t) => t.status === "DONE").length;
  const progress = data.tasks.length ? Math.round(doneAct / data.tasks.length * 100) : (event?.progress || 0);
  const contract = Number(event?.total_price) || 0;
  const paid = data.payments.filter((p) => p.status === "PAID").reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const pms = data.event_team.filter((m) => m.is_pm);

  if (!event) return <div className="text-[#7A7A7A]">Memuat event...</div>;

  return (
    <div>
      <button onClick={() => navigate("/events")} className="flex items-center gap-1 text-sm text-[#7A7A7A] hover:text-burgundy mb-4" data-testid="event-back"><ArrowLeft className="w-4 h-4" />Kembali ke Events</button>

      <div className="card-lux overflow-hidden mb-6 animate-fade-up">
        <div className="relative h-44 bg-gradient-to-br from-burgundy to-burgundy-dark flex items-end">
          <img src={photoSrc(event.cover) || "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"} alt="cover" className="absolute inset-0 w-full h-full object-cover opacity-30" style={{ objectPosition: event.cover_position || "center" }} />
          {can(3) && (
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              {event.cover && (
                <div className="flex items-center rounded-full bg-white/90 shadow-lg overflow-hidden" data-testid="event-cover-position">
                  {[["top", "Atas"], ["center", "Tengah"], ["bottom", "Bawah"]].map(([pos, lbl]) => (
                    <button key={pos} onClick={() => setCoverPos(pos)} data-testid={`cover-pos-${pos}`}
                      className={cn("px-2.5 py-1.5 text-[11px] font-semibold transition-colors", (event.cover_position || "center") === pos ? "bg-burgundy text-white" : "text-burgundy hover:bg-beige")}>{lbl}</button>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/90 hover:bg-white text-burgundy text-xs font-semibold cursor-pointer shadow-lg transition-colors" data-testid="event-cover-upload" title="Unggah / ganti poster event">
                {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {event.cover ? "Ganti Poster" : "Unggah Poster"}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingCover} onChange={(e) => { uploadCover(e.target.files); e.target.value = ""; }} />
              </label>
            </div>
          )}
          <div className="relative p-6 text-white w-full flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold-light">The Wedding of</p>
              <h1 className="font-heading text-4xl sm:text-5xl leading-none">{event.groom} & {event.bride}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/90">
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(event.wedding_date)}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.venue}</span>
                {pms.length > 0 && <span className="flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-gold-light" />PM: {pms.map((p) => p.member_name).join(", ")}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {can(3)
                ? <Select value={event.status || "Booked"} onValueChange={changeStatus}><SelectTrigger className="h-9 w-40 bg-white/90 text-burgundy border-0" data-testid="event-status-select"><SelectValue /></SelectTrigger><SelectContent>{EVENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                : <StatusBadge status={event.status} />}
              <Button onClick={() => navigate(`/events/${id}/day`)} data-testid="event-day-mode-btn" className="bg-gold hover:bg-gold-dark text-burgundy-dark rounded-full font-semibold"><Play className="w-4 h-4 mr-1" />Event Day Mode</Button>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="Overview">
        <div className="overflow-x-auto -mx-1 px-1 mb-4">
          <TabsList className="bg-beige inline-flex w-max">
            {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`tab-${t.toLowerCase()}`} className="data-[state=active]:bg-burgundy data-[state=active]:text-white">{t}</TabsTrigger>)}
          </TabsList>
        </div>

        {/* OVERVIEW */}
        <TabsContent value="Overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <OverviewStat label="Countdown" value={`${cd < 0 ? 0 : cd} hari`} icon={Zap} />
            <OverviewStat label="Progress" value={`${progress}%`} icon={CheckCircle2} />
            <div className="card-lux p-4" data-testid="contract-card">
              <div className="flex items-center justify-between text-[#7A7A7A] text-[11px] uppercase tracking-wider mb-1">
                <span className="flex items-center gap-2"><Gem className="w-3.5 h-3.5" />Total Contract</span>
                {can(4) && !editContract && <button onClick={() => { setContractVal(String(contract)); setEditContract(true); }} data-testid="contract-edit-btn" className="text-burgundy hover:text-burgundy-dark"><Pencil className="w-3.5 h-3.5" /></button>}
              </div>
              {editContract ? (
                <div className="flex items-center gap-1">
                  <input type="number" value={contractVal} onChange={(e) => setContractVal(e.target.value)} data-testid="contract-input" className="w-full border border-gold/40 rounded px-2 py-1 text-sm" autoFocus />
                  <button onClick={saveContract} data-testid="contract-save" className="text-emerald-600 p-1"><CheckCircle2 className="w-4 h-4" /></button>
                  <button onClick={() => setEditContract(false)} className="text-[#7A7A7A] p-1"><X className="w-4 h-4" /></button>
                </div>
              ) : <p className="font-heading text-xl text-[#1A1A1A] leading-none">{fmtIDR(contract)}</p>}
            </div>
            <OverviewStat label="Terbayar" value={fmtIDR(paid)} icon={Gem} />
          </div>
          {pms.length > 0 && (
            <div className="card-lux p-4 mb-3 flex items-center gap-3" data-testid="overview-pm">
              <Crown className="w-5 h-5 text-gold-dark" />
              <div><p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">Project Manager / Koordinator</p>
                <p className="text-sm font-semibold text-burgundy">{pms.map((p) => `${p.member_name} (${p.role})`).join(", ")}</p></div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniCount label="Activity" value={`${doneAct}/${data.tasks.length}`} to="/activity" />
            <MiniCount label="Vendor" value={data.event_vendors.length} />
            <MiniCount label="Tim" value={data.event_team.length} />
            <MiniCount label="Rundown" value={data.rundown.length} to="/rundown" />
            <MiniCount label="Dokumen" value={data.documents.length} to="/documents" />
            <MiniCount label="Sisa Bayar" value={fmtIDR(Math.max(contract - paid, 0))} to="/payments" />
          </div>
          <div className="mt-5 card-lux p-5">
            <div className="flex justify-between text-sm mb-1.5"><span className="text-[#7A7A7A]">Progress Persiapan</span><span className="font-semibold text-burgundy">{progress}%</span></div>
            <Progress value={progress} className="h-2 bg-beige" />
          </div>
        </TabsContent>

        {/* CLIENT */}
        <TabsContent value="Client">
          <div className="card-lux p-6 max-w-2xl">
            {client ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {[["Pengantin", `${client.groom} & ${client.bride}`], ["WhatsApp", client.phone], ["Email", client.email], ["Alamat", client.address], ["Orang Tua", client.parent_name], ["Kontak Ortu", client.parent_phone], ["Tanggal Booking", fmtDate(client.booking_date)], ["Status", client.status]].map(([l, v]) => (
                  <div key={l}><p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold">{l}</p><p className="text-[#1A1A1A] mt-0.5">{v || "-"}</p></div>
                ))}
                <div className="sm:col-span-2 flex gap-2 pt-2">
                  <a href={`tel:${client.phone}`} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-burgundy text-white text-sm" data-testid="client-call"><Phone className="w-4 h-4" />Call</a>
                  <a href={waLink(client.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm" data-testid="client-wa"><MessageCircle className="w-4 h-4" />WhatsApp</a>
                </div>
              </div>
            ) : <p className="text-[#7A7A7A]">Data client tidak tertaut. Tautkan client melalui halaman <Link to="/clients" className="text-burgundy underline">Clients</Link>.</p>}
          </div>
        </TabsContent>

        {/* TEAM */}
        <TabsContent value="Team">
          {can(3) && (
            <div className="flex justify-end mb-3">
              <Button onClick={() => setTeamOpen(true)} data-testid="pilih-tim-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><UserPlus className="w-4 h-4 mr-1" />Pilih Tim</Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.event_team.map((m) => (
              <div key={m.id} className={cn("card-lux p-4 relative", m.is_pm && "ring-2 ring-gold")} data-testid="event-team-card">
                {m.is_pm && <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold text-gold-dark bg-gold/15 px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" />PM</span>}
                <div className="flex items-center gap-3">
                  {(() => { const mp = photoSrc(m.photo) || photoSrc((masterTeam.find((x) => x.id === m.member_id) || {}).photo); return mp
                    ? <img src={mp} alt={m.member_name} className="w-12 h-12 rounded-full object-cover border border-gold/30" />
                    : <div className="w-12 h-12 rounded-full bg-beige text-burgundy font-semibold flex items-center justify-center">{initials(m.member_name)}</div>; })()}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">{m.member_name}</p>
                    <p className="text-xs text-gold-dark">{m.role}</p>
                    {m.member_id && (crewAccountIds.has(m.member_id)
                      ? <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="team-account-status-yes"><CheckCircle2 className="w-3 h-3" />Akun aktif</span>
                      : <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="team-account-status-no"><KeyRound className="w-3 h-3" />Belum ada akun</span>)}
                  </div>
                </div>
                {m.task && <p className="text-xs text-[#4A4A4A] mt-2"><span className="text-gold-dark font-semibold">Tugas: </span>{m.task}</p>}
                {m.notes && <p className="text-xs text-[#7A7A7A] mt-1">{m.notes}</p>}
                {can(3) && (
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => togglePM(m)} data-testid="team-toggle-pm" className={cn("text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1", m.is_pm ? "bg-gold/20 text-gold-dark" : "bg-beige text-[#4A4A4A] hover:text-burgundy")}><Star className="w-3 h-3" />{m.is_pm ? "Batalkan PM" : "Jadikan PM"}</button>
                    {can(4) && <button onClick={() => openAccount(m)} data-testid="team-account-btn" title="Buat / atur akun login crew" className="text-xs px-2.5 py-1 rounded-full bg-beige text-[#4A4A4A] hover:text-burgundy inline-flex items-center gap-1"><KeyRound className="w-3 h-3" />Akun Login</button>}
                    <button onClick={() => delTeam(m.id)} data-testid="team-remove" className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            ))}
            {data.event_team.length === 0 && <div className="card-lux p-8 text-center text-[#7A7A7A] col-span-full">Belum ada tim. {can(3) && "Klik \"Pilih Tim\" untuk menambahkan."}</div>}
          </div>
        </TabsContent>

        {/* VENDOR */}
        <TabsContent value="Vendor">
          {can(3) && (
            <div className="flex justify-end mb-3">
              <Button onClick={() => setVendorOpen(true)} data-testid="pilih-vendor-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Pilih Vendor</Button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.event_vendors.map((v) => (
              <div key={v.id} className="card-lux p-4" data-testid="event-vendor-card">
                <div className="flex items-center justify-between mb-2">
                  <div><h4 className="font-semibold text-[#1A1A1A]">{v.vendor_name}</h4>{v.category && <p className="text-[11px] text-gold-dark">{v.category}</p>}</div>
                  <StatusBadge status={v.confirmation} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-[#7A7A7A] mb-2">
                  {v.pic && <div><p className="text-gold-dark font-semibold">PIC</p>{v.pic}</div>}
                  {v.contact && <div><p className="text-gold-dark font-semibold">Kontak</p>{v.contact}</div>}
                </div>
                {v.notes && <p className="text-xs text-[#7A7A7A] mb-2">{v.notes}</p>}
                <p className="text-sm font-medium text-[#1A1A1A] mb-3">{fmtIDR(v.price)}</p>
                {can(3) && (
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={v.confirmation || "NOT CONFIRMED"} onChange={(e) => setVendorConfirm(v, e.target.value)} data-testid="vendor-confirm-select" className="text-xs border border-gold/30 rounded-lg px-2 py-1 bg-white">
                      {CONFIRMATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => remindVendor(v.id)} data-testid="vendor-remind-wa" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white"><Send className="w-3 h-3" />WA</button>
                    <button onClick={() => delVendor(v.id)} data-testid="vendor-remove" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-red-50 text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            ))}
            {data.event_vendors.length === 0 && <div className="card-lux p-8 text-center text-[#7A7A7A] md:col-span-2">Belum ada vendor. {can(3) && "Klik \"Pilih Vendor\" untuk menambahkan."}</div>}
          </div>
        </TabsContent>

        {/* RUNDOWN */}
        <TabsContent value="Rundown">
          <SimpleList empty="Belum ada rundown." link="/rundown" items={data.rundown.sort((a, b) => (a.order || 0) - (b.order || 0)).map((r) => ({ id: r.id, pre: r.end_time ? `${r.time}–${r.end_time}` : r.time, main: r.activity, sub: `${r.pic || ""} • ${r.location || ""}`, badge: r.status }))} />
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="Activity">
          <SimpleList empty="Belum ada activity." link="/activity" items={data.tasks.map((t) => ({ id: t.id, main: t.name, sub: `${t.category ? t.category + " • " : ""}${t.pic || "-"}`, badge: t.status }))} />
        </TabsContent>

        {/* TIMELINE */}
        <TabsContent value="Timeline">
          {can(3) && (
            <div className="card-lux p-4 mb-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
              <div className="flex-1">
                <label className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold">{tlEdit ? "Edit Milestone" : "Milestone Custom"}</label>
                <input value={tlLabel} onChange={(e) => setTlLabel(e.target.value)} data-testid="timeline-label" placeholder="Nama milestone" className="w-full mt-1 border border-gold/30 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
              <div className="w-full sm:w-40">
                <label className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold">Hari sebelum (T-)</label>
                <input type="number" value={tlOffset} onChange={(e) => setTlOffset(e.target.value)} data-testid="timeline-offset" placeholder="mis. 45" className="w-full mt-1 border border-gold/30 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
              <Button onClick={addTimeline} data-testid="timeline-add" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full">{tlEdit ? "Simpan" : "Tambah"}</Button>
              {tlEdit && <Button variant="outline" onClick={cancelEditTl} data-testid="timeline-cancel" className="rounded-full">Batal</Button>}
            </div>
          )}
          {can(3) && data.timeline.length === 0 && (
            <div className="card-lux p-4 mb-4 flex items-center justify-between gap-3 bg-gold/5">
              <p className="text-sm text-[#4A4A4A]">Timeline masih memakai template default. Aktifkan agar setiap milestone bisa Anda edit & hapus.</p>
              <Button onClick={initTimeline} data-testid="timeline-init" className="bg-gold hover:bg-gold-dark text-burgundy-dark rounded-full font-semibold shrink-0"><Pencil className="w-4 h-4 mr-1" />Aktifkan Edit Timeline</Button>
            </div>
          )}
          <div className="card-lux p-6">
            <div className="relative pl-6 space-y-5">
              <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gold/40" />
              {(data.timeline.length > 0
                ? data.timeline.map((it) => ({ label: it.label, off: Number(it.offset || 0), editable: true, id: it.id }))
                : TIMELINE.map(([t, label, off]) => ({ label, off, editable: false }))
              ).sort((a, b) => b.off - a.off).map((item, i) => {
                const target = new Date(new Date(event.wedding_date).getTime() - item.off * 86400000);
                const passed = daysUntil(target.toISOString()) < 0;
                const tlabel = item.off === 0 ? "T-0" : (item.off > 0 ? `T-${item.off}` : `T+${-item.off}`);
                return (
                  <div key={item.id || i} className="relative flex items-start gap-4 group" data-testid="timeline-item">
                    <span className={cn("absolute -left-6 w-4 h-4 rounded-full border-2 mt-0.5", passed ? "bg-burgundy border-burgundy" : "bg-gold border-gold")} />
                    <div className="w-14 font-mono text-sm font-semibold text-burgundy shrink-0">{tlabel}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#1A1A1A]">{item.label}</p>
                      <p className="text-xs text-[#7A7A7A]">{fmtDate(target.toISOString())}</p>
                    </div>
                    {item.editable && can(3) && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                        <button onClick={() => startEditTl(item)} className="text-[#7A7A7A] hover:text-burgundy" data-testid="timeline-edit"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => delTimeline(item.id)} className="text-[#7A7A7A] hover:text-red-600" data-testid="timeline-delete"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* PAYMENT */}
        <TabsContent value="Payment">
          <div className="flex items-center justify-between mb-3">
            <div className="grid grid-cols-3 gap-3 flex-1 max-w-xl">
              <PayStat label="Contract" value={fmtIDR(contract)} />
              <PayStat label="Paid" value={fmtIDR(paid)} color="text-emerald-600" />
              <PayStat label="Sisa" value={fmtIDR(Math.max(contract - paid, 0))} color="text-amber-600" />
            </div>
            {can(4) && <Button onClick={() => genInvoice()} data-testid="event-generate-invoice" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full ml-3 shrink-0"><Gem className="w-4 h-4 mr-1" />Buat Invoice</Button>}
          </div>
          <div className="card-lux divide-y divide-gold/10">
            {data.payments.length === 0 ? <p className="p-8 text-center text-[#7A7A7A]">Belum ada pembayaran.</p> :
              data.payments.map((p) => (
                <div key={p.id} className="px-5 py-3 flex items-center gap-3" data-testid="event-payment-item">
                  <span className="font-mono text-sm font-semibold text-burgundy w-28 shrink-0">{fmtIDR(p.amount)}</span>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#1A1A1A] truncate">{p.label}</p><p className="text-xs text-[#7A7A7A]">Jatuh tempo {fmtDate(p.due_date)}</p></div>
                  <StatusBadge status={p.status} />
                  {can(2) && p.status !== "PAID" && <button onClick={() => remindPayment(p.id)} data-testid="payment-remind-wa" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white shrink-0"><Send className="w-3 h-3" />WA</button>}
                  {can(4) && <button onClick={() => makeReceipt(p.id)} title="Buat Kuitansi" data-testid="event-make-receipt" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-burgundy text-white shrink-0"><Gem className="w-3 h-3" />Kuitansi</button>}
                </div>
              ))}
          </div>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="Documents">
          <SimpleList empty="Belum ada dokumen." link="/documents" items={data.documents.map((d) => ({ id: d.id, main: d.name, sub: d.type }))} />
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="Notes">
          <div className="card-lux p-5 max-w-2xl">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={8} data-testid="event-notes" placeholder="Catatan event..." disabled={!can(3)} />
            {can(3) && <Button onClick={saveNotes} data-testid="event-notes-save" className="mt-3 bg-burgundy hover:bg-burgundy-dark text-white rounded-full">Simpan Catatan</Button>}
          </div>
        </TabsContent>
      </Tabs>

      <PilihTimDialog open={teamOpen} onOpenChange={setTeamOpen} eventId={id} master={masterTeam} assigned={data.event_team} onDone={loadAll} refreshMaster={() => list("team_members").then(setMasterTeam)} />
      <PilihVendorDialog open={vendorOpen} onOpenChange={setVendorOpen} eventId={id} master={masterVendors} onDone={loadAll} refreshMaster={() => list("vendors").then(setMasterVendors)} />

      <Dialog open={!!acc} onOpenChange={(o) => !o && setAcc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-burgundy">Akun Login Crew</DialogTitle>
            <DialogDescription>Buat akun agar {acc?.name} bisa masuk ke Portal Crew dan melihat event & tugasnya. Ini menautkan akun ke anggota tim secara pasti.</DialogDescription>
          </DialogHeader>
          {acc?.exists && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="event-team-account-exists">
              <CheckCircle2 className="w-4 h-4" />Akun sudah ada: {acc.email}. Simpan lagi untuk reset password.
            </div>
          )}
          <div className="space-y-3 py-1">
            <div><Label className="text-xs font-semibold mb-1 block">Email Login</Label><Input value={acc?.email || ""} onChange={(e) => setAcc({ ...acc, email: e.target.value })} placeholder="crew@email.com" data-testid="event-team-account-email" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Password {acc?.exists ? "Baru" : ""}</Label><Input type="password" value={accPwd} onChange={(e) => setAccPwd(e.target.value)} placeholder="Minimal 6 karakter" data-testid="event-team-account-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcc(null)}>Batal</Button>
            <Button onClick={saveAccount} disabled={accSaving} data-testid="event-team-account-save" className="bg-burgundy hover:bg-burgundy-dark text-white">{accSaving ? "Menyimpan..." : (acc?.exists ? "Perbarui Akun" : "Buat Akun")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PilihTimDialog({ open, onOpenChange, eventId, master, assigned, onDone, refreshMaster }) {
  const [memberId, setMemberId] = useState("");
  const [role, setRole] = useState("");
  const [isPm, setIsPm] = useState(false);
  const [notes, setNotes] = useState("");
  const [nn, setNn] = useState({ name: "", role: "Crew", phone: "", photo: "" });

  const assign = async () => {
    if (!memberId) { toast.error("Pilih anggota tim dulu"); return; }
    const m = master.find((x) => x.id === memberId);
    await create("event_team", { event_id: eventId, member_id: memberId, member_name: m?.name, photo: m?.photo || "", role: role || m?.role || "Crew", is_pm: isPm, status: "Active", notes });
    toast.success("Tim ditambahkan"); setMemberId(""); setRole(""); setIsPm(false); setNotes(""); onDone();
  };
  const addNew = async () => {
    if (!nn.name.trim()) { toast.error("Nama wajib diisi"); return; }
    const created = await create("team_members", { ...nn, skill: "", status: "Active" });
    toast.success("Anggota tim baru dibuat"); await refreshMaster(); setNn({ name: "", role: "Crew", phone: "", photo: "" }); setMemberId(created.id);
  };
  const assignedIds = new Set(assigned.map((a) => a.member_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">Pilih Tim untuk Event</DialogTitle><DialogDescription>Tugaskan anggota tim ke event dan tetapkan Project Manager/Koordinator.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-3 border border-gold/20 rounded-xl p-4">
            <p className="text-xs font-semibold text-gold-dark uppercase tracking-wider">Tugaskan dari Master Tim</p>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Anggota</Label>
              <Select value={memberId} onValueChange={setMemberId}>
                <SelectTrigger data-testid="team-member-select"><SelectValue placeholder="Pilih anggota tim..." /></SelectTrigger>
                <SelectContent>{master.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {m.role}{assignedIds.has(m.id) ? " (sudah ada)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Peran di Event</Label><Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="mis. Coordinator" data-testid="team-role-input" /></div>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Catatan</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="team-notes-input" /></div>
            <label className="flex items-center gap-2 text-sm text-[#4A4A4A]"><input type="checkbox" checked={isPm} onChange={(e) => setIsPm(e.target.checked)} data-testid="team-pm-checkbox" />Tetapkan sebagai Project Manager / Koordinator</label>
            <Button onClick={assign} data-testid="team-assign-btn" className="w-full bg-burgundy hover:bg-burgundy-dark text-white rounded-full">Tambahkan ke Event</Button>
          </div>

          <div className="space-y-3 border border-dashed border-gold/30 rounded-xl p-4">
            <p className="text-xs font-semibold text-gold-dark uppercase tracking-wider">Tambah Anggota Tim Baru</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Nama</Label><Input value={nn.name} onChange={(e) => setNn({ ...nn, name: e.target.value })} data-testid="new-member-name" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Peran</Label><Input value={nn.role} onChange={(e) => setNn({ ...nn, role: e.target.value })} data-testid="new-member-role" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">No. WhatsApp</Label><Input value={nn.phone} onChange={(e) => setNn({ ...nn, phone: e.target.value })} data-testid="new-member-phone" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Foto Profil</Label>
                <FileField value={nn.photo} filename={nn.photo ? "foto" : ""} name="member-photo" accept="image/*" onUploaded={(res) => setNn({ ...nn, photo: res.storage_path })} onClear={() => setNn({ ...nn, photo: "" })} />
              </div>
            </div>
            <Button onClick={addNew} variant="outline" data-testid="new-member-save" className="w-full rounded-full">Simpan Anggota Baru</Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PilihVendorDialog({ open, onOpenChange, eventId, master, onDone, refreshMaster }) {
  const [vendorId, setVendorId] = useState("");
  const [pic, setPic] = useState("");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState("NOT CONFIRMED");
  const [notes, setNotes] = useState("");
  const [nv, setNv] = useState({ name: "", category: "Others", contact_person: "", phone: "" });

  const assign = async () => {
    if (!vendorId) { toast.error("Pilih vendor dulu"); return; }
    const v = master.find((x) => x.id === vendorId);
    await create("event_vendors", { event_id: eventId, vendor_id: vendorId, vendor_name: v?.name, category: v?.category || "", contact: v?.phone || "", pic: pic || v?.contact_person || "", price: Number(price || v?.price || 0), confirmation: status, notes });
    toast.success("Vendor ditambahkan"); setVendorId(""); setPic(""); setPrice(""); setStatus("NOT CONFIRMED"); setNotes(""); onDone();
  };
  const addNew = async () => {
    if (!nv.name.trim()) { toast.error("Nama vendor wajib diisi"); return; }
    const created = await create("vendors", { ...nv, email: "", address: "", price: 0, rating: 0, status: "Active", notes: "", portfolio: "" });
    toast.success("Vendor baru dibuat"); await refreshMaster(); setNv({ name: "", category: "Others", contact_person: "", phone: "" }); setVendorId(created.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">Pilih Vendor untuk Event</DialogTitle><DialogDescription>Pilih vendor dari master dan atur detail kerja sama untuk event ini.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-3 border border-gold/20 rounded-xl p-4">
            <p className="text-xs font-semibold text-gold-dark uppercase tracking-wider">Pilih dari Master Vendor</p>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="vendor-select"><SelectValue placeholder="Pilih vendor..." /></SelectTrigger>
                <SelectContent>{master.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} — {v.category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">PIC</Label><Input value={pic} onChange={(e) => setPic(e.target.value)} data-testid="vendor-pic-input" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Biaya (Rp)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="vendor-price-input" /></div>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="vendor-status-input"><SelectValue /></SelectTrigger>
                <SelectContent>{CONFIRMATION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Catatan</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="vendor-notes-input" /></div>
            <Button onClick={assign} data-testid="vendor-assign-btn" className="w-full bg-burgundy hover:bg-burgundy-dark text-white rounded-full">Tambahkan ke Event</Button>
          </div>

          <div className="space-y-3 border border-dashed border-gold/30 rounded-xl p-4">
            <p className="text-xs font-semibold text-gold-dark uppercase tracking-wider">Tambah Vendor Baru</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Nama</Label><Input value={nv.name} onChange={(e) => setNv({ ...nv, name: e.target.value })} data-testid="new-vendor-name" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Kategori</Label>
                <Select value={nv.category} onValueChange={(v) => setNv({ ...nv, category: v })}>
                  <SelectTrigger data-testid="new-vendor-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{VENDOR_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-semibold mb-1 block">PIC</Label><Input value={nv.contact_person} onChange={(e) => setNv({ ...nv, contact_person: e.target.value })} data-testid="new-vendor-pic" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">No. WhatsApp</Label><Input value={nv.phone} onChange={(e) => setNv({ ...nv, phone: e.target.value })} data-testid="new-vendor-phone" /></div>
            </div>
            <Button onClick={addNew} variant="outline" data-testid="new-vendor-save" className="w-full rounded-full">Simpan Vendor Baru</Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OverviewStat = ({ label, value, icon: Icon }) => (
  <div className="card-lux p-4">
    <div className="flex items-center gap-2 text-[#7A7A7A] text-[11px] uppercase tracking-wider mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
    <p className="font-heading text-xl text-[#1A1A1A] leading-none">{value}</p>
  </div>
);
const MiniCount = ({ label, value, to }) => {
  const inner = (
    <div className="card-lux hover-lift p-4">
      <p className="font-heading text-2xl text-burgundy leading-none">{value}</p>
      <p className="text-xs text-[#7A7A7A] mt-1">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};
const PayStat = ({ label, value, color }) => (
  <div className="card-lux p-4"><p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">{label}</p><p className={cn("font-heading text-xl mt-0.5 leading-none", color || "text-[#1A1A1A]")}>{value}</p></div>
);
function SimpleList({ items, empty, link }) {
  return (
    <div className="card-lux divide-y divide-gold/10">
      {items.length === 0 ? <p className="p-8 text-center text-[#7A7A7A]">{empty}{link && <> <Link to={link} className="text-burgundy underline">Kelola di sini</Link>.</>}</p> :
        items.map((it) => (
          <div key={it.id} className="px-5 py-3 flex items-center gap-3" data-testid="simple-list-item">
            {it.pre && <span className="font-mono text-sm font-semibold text-burgundy w-24 shrink-0">{it.pre}</span>}
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#1A1A1A] truncate">{it.main}</p>{it.sub && <p className="text-xs text-[#7A7A7A]">{it.sub}</p>}</div>
            {it.badge && <StatusBadge status={it.badge} />}
          </div>
        ))}
    </div>
  );
}
